<script setup lang="ts">
import { computed } from 'vue'
import { Loader2 } from 'lucide-vue-next'
import { Badge } from '@/components/ui/badge'
import { calculateArea } from '@/utils/geometry'
import { formatNumber, formatQuota, formatDate, normalizeUnits, daysUntil } from './format'
import type { TaskingContract } from './types'
import type { PendingLayer, OrgQuota, PricingEstimate, QuotaLine, PlanetProduct, PlanetSubscription } from './types'

const props = withDefaults(defineProps<{
  layer: PendingLayer
  quota?: OrgQuota | null
  loading?: boolean
  pricing?: PricingEstimate | null
  mode?: 'bar' | 'details'
}>(), { mode: 'bar' })

const PV_KEYWORDS = ['forest_carbon', 'soil_water', 'land_surface', 'crop_biomass', 'vegetation', 'field_bound', 'phenology']
const EXPIRY_WARN_DAYS = 60
const MIN_LABEL_PCT = 4

const isTasking = computed(() => props.layer.layer_type === 'tasking')

const aoiKm2 = computed<number | null>(() => {
  if (props.layer.geometry) {
    const m2 = calculateArea(props.layer.geometry)
    if (m2 > 0) return m2 / 1_000_000
  }
  return props.layer.aoi_area_sqkm ?? null
})

const isPlanetaryVariable = computed(() =>
  PV_KEYWORDS.some(k => (props.layer.data?.type ?? '').toLowerCase().includes(k)))

const matchingProducts = (): PlanetProduct[] => {
  const products = props.quota?.products ?? []
  const itemType = (props.layer.data?.item_type ?? '').toLowerCase()
  if (!itemType) return products
  const matched = products.filter(p =>
    p.name?.toLowerCase().includes(itemType) ||
    p.product_family?.toLowerCase().includes(itemType) ||
    p.title?.toLowerCase().includes(itemType))
  return matched.length ? matched : products
}

const pvProducts = (): PlanetProduct[] => {
  const products = props.quota?.products ?? []
  const type = (props.layer.data?.type ?? '').toLowerCase()
  const paramId = (props.layer.data?.parameters?.id ?? '').toLowerCase()
  return products.filter(p => {
    const name = (p.name ?? '').toLowerCase()
    const family = (p.product_family ?? '').toLowerCase()
    const title = (p.title ?? '').toLowerCase()
    return PV_KEYWORDS.some(k => name.includes(k) || family.includes(k) || title.includes(k)) ||
      (type && (name.includes(type) || family.includes(type))) ||
      (paramId && name.includes(paramId))
  })
}

const subsWithQuota = (): PlanetSubscription[] =>
  (props.quota?.subscriptions ?? []).filter(s => s.quota_sqkm && s.quota_sqkm > 0)

// Tasking contracts are denominated in credits, so the AOI area is not the
// quota cost — Planet's pricing estimate is. Only the live quote feeds the
// quota math: the stored estimate is client-submitted and may predate a
// capture-window edit, so it is shown for context only.
const storedCost = computed<number | null>(() => props.layer.data?.estimate?.estimated_cost ?? null)
const liveCost = computed<number | null>(() => {
  const p = props.pricing
  if (!p || p.loading || p.error || p.elapsed) return null
  return p.estimated_cost ?? null
})
const cost = liveCost
const costSource = computed(() => {
  if (liveCost.value == null) return null
  return props.pricing?.requoted ? 're-quoted from today' : 'live estimate'
})
const costMismatch = computed(() =>
  liveCost.value != null && storedCost.value != null && Math.abs(liveCost.value - storedCost.value) >= 1)

const areaCost = (units: string): number | null => {
  if (aoiKm2.value == null) return null
  if (units === 'km²') return aoiKm2.value
  if (units === 'ha') return aoiKm2.value * 100
  return null
}

const productLine = (p: PlanetProduct): QuotaLine => {
  const units = normalizeUnits(p.quota_units)
  return {
    key: `product-${p.id}`,
    label: p.title || p.name || `Product ${p.id}`,
    sublabel: p.product_family,
    unlimited: p.unlimited_quota,
    limit: p.quota_total,
    used: p.quota_used,
    remaining: p.quota_remaining,
    units,
    cost: areaCost(units),
    expiration: null,
    overage: null,
  }
}

const lines = computed<QuotaLine[]>(() => {
  if (!props.quota) return []
  if (isTasking.value) {
    return props.quota.taskingContracts.map(c => {
      const limit = c.quota_limit
      const used = c.quota_used ?? 0
      return {
        key: `contract-${c.id}`,
        label: c.name || c.sfdc || 'Tasking contract',
        sublabel: c.sfdc && c.name ? c.sfdc : null,
        unlimited: false,
        limit,
        used,
        remaining: c.quota_remaining ?? (limit != null ? limit - used : null),
        units: normalizeUnits(c.quota_units) || 'credits',
        cost: cost.value,
        expiration: c.expiration_date,
        overage: c.overage,
      }
    })
  }
  if (props.layer.layer_type === 'order') return matchingProducts().map(productLine)
  if (isPlanetaryVariable.value) return pvProducts().map(productLine)
  return subsWithQuota().map(s => ({
    key: `sub-${s.id}`,
    label: s.plan_name || `Subscription ${s.id}`,
    sublabel: null,
    unlimited: false,
    limit: s.quota_sqkm,
    used: s.used_sqkm,
    remaining: s.remaining_sqkm ?? (s.quota_sqkm != null ? s.quota_sqkm - (s.used_sqkm ?? 0) : null),
    units: 'km²',
    cost: areaCost('km²'),
    expiration: s.end_time,
    overage: null,
  }))
})

// The Planet call that this layer type's quota comes from, so a failed fetch
// reads as "couldn't load" rather than "no quota".
const fetchError = computed<string | null>(() => {
  const errors = props.quota?.errors
  if (!errors) return null
  if (isTasking.value) return errors.taskingContracts ?? null
  if (props.layer.layer_type === 'order' || isPlanetaryVariable.value) return errors.products ?? null
  return errors.subscriptions ?? null
})

const emptyLabel = computed(() => {
  if (isTasking.value) return 'No active tasking contracts'
  if (props.layer.layer_type === 'order') return 'No quota data'
  return isPlanetaryVariable.value ? 'No quota data from Planet' : 'No active download quota'
})

const pct = (value: number | null, limit: number | null): number =>
  value == null || !limit ? 0 : Math.max(0, Math.min(100, (value / limit) * 100))

// Bar semantics: the filled part is what the org still has; the request eats
// into it from the right, so what stays solid is what remains after approval.
const derived = (line: QuotaLine) => {
  const after = line.remaining != null && line.cost != null ? line.remaining - line.cost : null
  const exceeds = line.cost != null && line.remaining != null && line.cost > line.remaining
  const usedPct = pct(line.used, line.limit)
  const remainingPct = pct(line.remaining, line.limit)
  const costPct = Math.min(remainingPct, pct(line.cost, line.limit))
  const afterPct = Math.max(0, remainingPct - costPct)
  const expiresIn = daysUntil(line.expiration)
  const endTime = props.layer.data?.end_time
  const expiresBeforeWindow = !!(isTasking.value && line.expiration && endTime && new Date(line.expiration) < new Date(endTime))
  return { after, exceeds, usedPct, remainingPct, costPct, afterPct, expiresIn, expiresBeforeWindow }
}

const expiryLabel = (line: QuotaLine): string | null => {
  const days = daysUntil(line.expiration)
  if (days == null || days >= EXPIRY_WARN_DAYS) return null
  return days < 0 ? 'Expired' : `Expires in ${days} days`
}

const costUnits = computed(() =>
  normalizeUnits(props.pricing?.units || props.layer.data?.estimate?.units) || 'credits')

const contracts = computed<TaskingContract[]>(() => isTasking.value ? (props.quota?.taskingContracts ?? []) : [])
</script>

<template>
  <!-- Bar mode: the decision-critical view — how much this request takes out of what is left -->
  <div v-if="mode === 'bar'" class="space-y-2">
    <div v-if="loading" class="flex items-center gap-1 text-xs text-muted-foreground">
      <Loader2 class="h-3 w-3 animate-spin" />
      Loading quota…
    </div>
    <div v-else-if="quota?.error" class="text-xs text-muted-foreground italic">No Planet API key for this org</div>
    <div v-else-if="fetchError" class="text-xs text-destructive">
      Couldn't load quota from Planet: {{ fetchError }} — press Refresh to retry.
    </div>
    <div v-else-if="!lines.length" class="text-xs text-muted-foreground italic">{{ emptyLabel }}</div>

    <div v-for="line in lines" :key="line.key" class="space-y-1">
      <div class="flex items-center justify-between gap-3 text-xs">
        <span class="font-medium truncate" :title="line.label">{{ line.label }}</span>
        <span v-if="line.unlimited" class="whitespace-nowrap">Unlimited</span>
      </div>

      <template v-if="!line.unlimited">
        <div class="h-6 w-full rounded-md bg-muted overflow-hidden flex text-[11px] font-medium leading-6" :title="`Remaining ${Math.round(derived(line).remainingPct)}% · this request ${Math.round(derived(line).costPct)}%`">
          <div class="h-full bg-primary text-white text-center truncate px-1" :style="{ width: `${derived(line).afterPct}%` }">
            <span v-if="derived(line).afterPct >= MIN_LABEL_PCT">{{ formatNumber(derived(line).after, 0) }}</span>
          </div>
          <div
            v-if="line.cost"
            class="h-full shrink-0 min-w-[1.75rem] animate-pulse text-white text-center truncate px-0.5"
            :class="derived(line).exceeds ? 'bg-destructive/60' : 'bg-primary/45'"
            :style="{ width: `${derived(line).costPct}%` }"
          >{{ formatNumber(line.cost, 0) }}</div>
          <div class="h-full flex-1 text-muted-foreground text-center truncate px-1">
            <span v-if="derived(line).usedPct >= MIN_LABEL_PCT">{{ formatNumber(line.used, 0) }} used</span>
          </div>
        </div>
        <div class="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs">
          <div class="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span v-if="isTasking && pricing?.loading" class="flex items-center gap-1 text-muted-foreground"><Loader2 class="h-3 w-3 animate-spin" />Pricing…</span>
            <span v-else-if="derived(line).after != null" class="flex items-center gap-1" :class="derived(line).exceeds ? 'text-destructive' : ''">
              <span class="h-2 w-2 rounded-full bg-primary"></span>
              <strong>{{ formatQuota(derived(line).after, line.units) }}</strong> left after approval
            </span>
            <span v-else-if="isTasking && pricing?.loading" class="text-muted-foreground italic">Fetching live quote…</span>
            <span v-else class="text-muted-foreground italic">No cost estimate</span>
            <span v-if="line.cost != null" class="flex items-center gap-1 text-muted-foreground">
              <span class="h-2 w-2 rounded-full" :class="derived(line).exceeds ? 'bg-destructive/60' : 'bg-primary/45'"></span>
              This request {{ formatNumber(line.cost, 0) }}
            </span>
            <span class="flex items-center gap-1 text-muted-foreground">
              <span class="h-2 w-2 rounded-full bg-muted-foreground/40"></span>
              Used {{ formatNumber(line.used, 0) }}
            </span>
            <Badge v-if="derived(line).exceeds && !line.overage" variant="destructive" class="text-[10px] px-1.5 py-0">Exceeds remaining</Badge>
            <Badge v-else-if="derived(line).exceeds && line.overage" variant="secondary" class="text-[10px] px-1.5 py-0">Exceeds remaining · overage allowed</Badge>
            <Badge v-if="derived(line).expiresBeforeWindow" variant="destructive" class="text-[10px] px-1.5 py-0">Expires before capture window</Badge>
            <Badge v-else-if="expiryLabel(line)" variant="secondary" class="text-[10px] px-1.5 py-0">{{ expiryLabel(line) }}</Badge>
          </div>
          <span class="whitespace-nowrap text-muted-foreground">
            <strong class="text-foreground">{{ formatQuota(line.remaining, line.units) }}</strong> of {{ formatNumber(line.limit, 2) }} left
          </span>
        </div>
      </template>
    </div>

    <div v-if="isTasking && pricing?.elapsed" class="text-xs text-destructive">Less than 14 days left in the capture window — change it before approving</div>
    <div v-else-if="isTasking && pricing?.error" class="text-xs text-destructive">Live pricing failed: {{ pricing.error }}</div>
    <div v-if="costMismatch" class="text-xs text-amber-600 dark:text-amber-400">
      User saw {{ formatNumber(storedCost, 2) }} {{ costUnits }} at request time; Planet now quotes {{ formatNumber(liveCost, 2) }}
    </div>
  </div>

  <!-- Details mode: the supporting numbers, shown inside the card's collapsible -->
  <div v-else class="text-xs space-y-0.5">
    <div class="text-muted-foreground font-medium">Quota</div>
    <template v-if="isTasking">
      <div v-if="cost != null"><span class="text-muted-foreground">Cost:</span> {{ formatNumber(cost, 2) }} {{ costUnits }}<span v-if="costSource" class="text-muted-foreground"> · {{ costSource }}</span></div>
      <div v-if="storedCost != null"><span class="text-muted-foreground">At request time:</span> {{ formatNumber(storedCost, 2) }} {{ costUnits }}</div>
      <div v-if="pricing?.area_km2 != null"><span class="text-muted-foreground">Planet billable area:</span> {{ formatNumber(pricing.area_km2, 2) }} km²</div>
    </template>
    <div v-if="aoiKm2 != null"><span class="text-muted-foreground">AOI:</span> {{ formatNumber(aoiKm2, 2) }} km²</div>
    <template v-for="line in lines" :key="line.key">
      <div class="pt-1 font-medium">{{ line.label }}<span v-if="line.sublabel" class="text-muted-foreground font-normal"> · {{ line.sublabel }}</span></div>
      <div v-if="line.unlimited">Unlimited</div>
      <template v-else>
        <div><span class="text-muted-foreground">Used:</span> {{ formatQuota(line.used, line.units) }} of {{ formatQuota(line.limit, line.units) }} ({{ Math.round(derived(line).usedPct) }}%)</div>
        <div v-if="derived(line).after != null"><span class="text-muted-foreground">After approval:</span> {{ formatQuota(derived(line).after, line.units) }} left ({{ Math.round(pct((line.used ?? 0) + (line.cost ?? 0), line.limit)) }}% used)</div>
        <div v-if="line.expiration"><span class="text-muted-foreground">Expires:</span> {{ formatDate(line.expiration) }}</div>
      </template>
    </template>
    <template v-for="c in contracts" :key="`c-${c.id}`">
      <div v-if="c.assured_quota_limit != null"><span class="text-muted-foreground">Assured quota:</span> {{ formatNumber(c.assured_quota_limit, 2) }} {{ normalizeUnits(c.quota_units) || 'credits' }}</div>
      <div v-if="c.overage != null"><span class="text-muted-foreground">Overage:</span> {{ c.overage ? 'allowed' : 'not allowed' }}</div>
      <div v-if="c.start_date"><span class="text-muted-foreground">Contract term:</span> {{ formatDate(c.start_date) }} — {{ formatDate(c.expiration_date) }}</div>
    </template>
  </div>
</template>
